import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { CsPage, NavController } from 'providers/nav-controller';
import { showCoachAlreadyLinkedAlertAsync, showOtherAccountAlertAsync, showStoreRefusedAlertAsync } from 'pages/subscribe/other-account-alert';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { formatMonthYearLong, formatDateMed } from '@cf-shared/date-format';
import { parseMonthKey } from '@cf-shared/date-format-machine';
import { CoachBillingService } from 'services/coach-billing.service';
import { LoggingService } from 'services/logging.service';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import {
  CoachBillingMonthlyStatementSummary,
  GetCoachBillingStateResult,
  CoachTierState,
  CoachSeatState,
} from '@cf-shared/coach-billing-interface';
import { CoachBalanceState } from '@cf-shared/data-model/coach-billing';
import { formatMinorCurrencyAmount } from '@cf-shared/data-model/coach-billing-notice';
import { CoachClientSlot } from '@cf-shared/data-model/coach';
import { UserDb } from 'services/user-db.service';
import { describeError } from '@cf-shared/error-describe';
import { computeClientsToPause } from 'util/coach-sleep-display';
import { untranslatedString } from 'models/translated-string';
import { CoachBillingPageParams } from './coach-billing-page-interface';
import { isCapacitorNative } from 'environments/environment-helpers';
import { CoachTierService, CoachTierOption } from 'services/coach-tier.service';
import { CoachTierOutcome, CoachTierOutcomeMessage, decideCoachTierOutcomeMessage } from 'services/coach-tier-outcome';
import { PopupMenuProvider, PopupMenuOptions } from 'providers/popup-menu/popup-menu';
import { DateTime } from 'luxon';
import { CoachHistoryRowView } from '@cf-shared/coach-history-interface';
import { CoachHistoryRoute } from '@cf-shared/data-model/coach-history';
import { buildMonthlyClientBars, MonthlyClientBar, newestBarCap } from '@cf-shared/data-model/coach-history-chart';
import { TelemetryService } from 'services/telemetry.service';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { openExternalLink } from 'util/open-external-link';
import { chargesForPastPayments, FOLDED_CHARGE_COUNT, formatMajorCurrencyAmount, storesInCharges } from './coach-billing-history';

/** Where the history cards are in loading (#6768). */
export enum CoachHistoryLoad {
  Loading = 'loading',
  Loaded = 'loaded',
  Failed = 'failed',
}

/** The two ranges of the Clients Over Time switch. */
export enum CoachHistoryRange {
  SixMonths = '6',
  TwelveMonths = '12',
}

/** The server answered without a link; the message is its error code, never the link. */
class CoachInvoiceUnavailableError extends Error {}

/** Change Plan has no message of its own for the two coach-only refusals
 *  (already owned, cross-provider conflict), so it treats them as if the codes
 *  were unknown. That is what it has always done: the coach is told the change
 *  may still apply when they were charged, and that it failed (or succeeded)
 *  according to `success` otherwise. */
function changePlanOutcomeMessage(result: CoachTierOutcome): CoachTierOutcomeMessage {
  const decided = decideCoachTierOutcomeMessage(result);
  if (decided !== CoachTierOutcomeMessage.AlreadyOwned && decided !== CoachTierOutcomeMessage.CrossProviderConflict) return decided;
  if (result.confirmationFailedAfterPurchase) return CoachTierOutcomeMessage.ConfirmationPending;
  return result.success ? CoachTierOutcomeMessage.Succeeded : CoachTierOutcomeMessage.Failed;
}

/**
 * Coach Billing dashboard (#3666 Phase 1, PRD §R12).
 *
 * Past the loading and load-error states, the template takes the FIRST
 * branch that matches, in this order:
 *   - unmigrated (no `coachBillingData`) → "Billing Coming Soon" card
 *   - an app-store fixed-tier coach → current tier, used/cap, Change Plan
 *   - a Paddle per-seat (web) coach → client-seat count + "billed monthly"
 *   - anyone else → the metered balance + statement history
 *
 * In practice a legacy `coachMaxSlots` coach lands on the FIRST of those,
 * not the metered one: nothing writes a `coachBillingData` block any more,
 * so `getCoachBillingState` reports them unmigrated and their
 * `coachMaxSlots` grant governs what they can do.
 *
 * That makes the metered view unreachable today. It is also read-only now:
 * it carries NO call to action, because the PayPro rail its "Set Up Monthly
 * Billing" / "Manage Payment Method" buttons drove was replaced by the
 * Paddle per-seat subscription (#3919) and removed in #4359.
 *
 * Three balance phrasings depending on derived state:
 *   - balance > 0 → "X.X client-months remaining" (with runout date
 *     when no payment method is on file)
 *   - balance <= 0 AND hasPaymentMethodOnFile → "X.X client-months due"
 *   - balance <= 0 AND !hasPaymentMethodOnFile → "Account locked"
 */
@Component({
  selector: 'page-coach-billing',
  templateUrl: 'coach-billing-page.html',
  host: { 'class': 'coach-billing-page' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoachBillingPage extends CsPage<CoachBillingPageParams> {
  protected LL = LL;

  loaded = false;
  loadError = false;
  isRefreshing = false;
  /** Latest successful load. Null until first load completes. */
  state: CoachBalanceState | null = null;
  history: CoachBillingMonthlyStatementSummary[] = [];
  /** R7.5 legacy passthrough — when true, hide CTAs and show the
   *  "not yet migrated" copy. */
  isUnmigrated = false;
  /** Set for an app-store (fixed-tier) coach. When present the dashboard
   *  renders the fixed-tier UI (current tier + used/cap + Change Plan)
   *  instead of the metered balance UI. */
  tierState: CoachTierState | null = null;
  /** Set for a Paddle per-seat (web) coach. When present the dashboard
   *  renders the seat UI (client-seat count + "billed monthly, prorated")
   *  instead of the metered or fixed-tier UI. */
  seatState: CoachSeatState | null = null;
  /** True while a Change Plan cross-grade is in flight. */
  isChangingPlan = false;
  /** True while the "some clients will be paused" confirmation is open (#4454).
   *  Deliberately separate from `isChangingPlan`, which greys out the Change
   *  Plan button — a dialog the coach is still reading should not do that. */
  private isConfirmingPlanChange = false;
  /** True on any Capacitor-native build (iOS or Android). The fixed-tier
   *  Change Plan cross-grade is an in-app store purchase, so it only
   *  appears here. Locked at construction so the template can branch on
   *  it without re-evaluating per change detection. */
  readonly isNative: boolean = isCapacitorNative();

  // ── the history cards (#6768) ──
  readonly CoachHistoryLoad = CoachHistoryLoad;
  readonly CoachHistoryRange = CoachHistoryRange;
  readonly CoachHistoryRoute = CoachHistoryRoute;
  historyLoad = CoachHistoryLoad.Loading;
  /** The range the switch shows; 6 months each time the page opens. */
  historyRange = CoachHistoryRange.SixMonths;
  /** All rows the server sent, oldest first. */
  private historyRows: CoachHistoryRowView[] = [];
  /** The bars of the selected range, rebuilt when the rows or the range change. */
  clientBars: MonthlyClientBar[] = [];
  /** The charges of the last 12 months, newest first. */
  charges: CoachHistoryRowView[] = [];
  /** Each listed charge's amount line, built when the history loads. */
  private chargeLines = new Map<CoachHistoryRowView, string>();
  /** The stores that appear in the charges, for one receipts line each. */
  receiptStores: (CoachHistoryRoute.Apple | CoachHistoryRoute.GooglePlay)[] = [];
  /** Whether Past Payments is unfolded; opened by the card's own arrow. */
  paymentsExpanded = false;
  readonly foldedChargeCount = FOLDED_CHARGE_COUNT;
  /** The charge whose invoice is being fetched, so a second tap waits. */
  private openingInvoiceFor: string | undefined;

  constructor(
    public navCtrl: NavController,
    private coachBilling: CoachBillingService,
    private coachTier: CoachTierService,
    private userDb: UserDb,
    private popupCtrl: PopupMenuProvider,
    private alertCtrl: AlertController,
    private logging: LoggingService,
    private telemetry: TelemetryService,
    private toastCtrl: ToastController,
    private cd: ChangeDetectorRef,
  ) {
    super();
  }

  async ionViewWillEnter() {
    // dedupe against a prior in-flight load — back-then-forward
    // navigation can fire ionViewWillEnter again while the earlier
    // round-trip is still settling, and the later-arriving response
    // would otherwise overwrite the fresher one
    if (this.isRefreshing) return;
    await this.loadStateAsync();
  }

  /** Pull-to-refresh and the header refresh button both call this. */
  async refreshTappedAsync() {
    if (this.isRefreshing) return;
    await this.loadStateAsync();
  }

  private async loadStateAsync() {
    this.isRefreshing = true;
    this.cd.markForCheck();
    try {
      const result: GetCoachBillingStateResult = await this.coachBilling.loadStateAsync();
      this.state = result.state;
      this.history = result.history;
      this.isUnmigrated = result.isUnmigrated;
      this.tierState = result.tierState ?? null;
      this.seatState = result.seatState ?? null;
      this.loadError = false;
      this.loaded = true;
      // the history cards sit under the plan cards of these two views only
      if (this.tierState || this.seatState) {
        void this.loadHistoryAsync();
      }
    } catch (err) {
      // surface to telemetry per CLAUDE.md "unexpected conditions
      // must surface". Keep the previous successful state on screen
      // so a transient network error does not blank the dashboard.
      this.logging.trackAbnormalException(err);
      this.loadError = true;
    } finally {
      this.isRefreshing = false;
      this.cd.markForCheck();
    }
  }

  /**
   * Load the history for the two cards, after the plan state. A failure shows an
   * inline error with Retry under the plan cards and never blanks them; an older
   * server without the function answers 404 and is treated the same way.
   */
  async loadHistoryAsync() {
    this.historyLoad = CoachHistoryLoad.Loading;
    this.cd.markForCheck();
    try {
      const result = await this.coachBilling.loadHistoryAsync();
      if (this.viewDestroyed) return;
      // the server always sends a list, so a reply without one is a failure, not an empty history
      if (!Array.isArray(result?.rows)) {
        throw new Error('coach history reply has no rows list');
      }
      this.historyRows = result.rows;
      this.rebuildHistoryViews();
      this.historyLoad = CoachHistoryLoad.Loaded;
    } catch (err) {
      if (this.viewDestroyed) return;
      this.logging.trackSilentProblem('CoachHistoryLoadFailed', { message: describeError(err) });
      this.historyLoad = CoachHistoryLoad.Failed;
    } finally {
      this.cd.markForCheck();
    }
  }

  /** The 6 / 12 Months switch. */
  onHistoryRangeChanged(range: CoachHistoryRange) {
    this.historyRange = range;
    this.rebuildHistoryViews();
    this.cd.markForCheck();
  }

  /** Derive everything the two cards show from the loaded rows: the bars of the selected
   *  range, the listed charges, the receipts lines and each charge's amount line. Runs when the
   *  history loads and when the range switch changes. */
  private rebuildHistoryViews() {
    const now = DateTime.local();
    const months = this.historyRange === CoachHistoryRange.TwelveMonths ? 12 : 6;
    this.clientBars = buildMonthlyClientBars(this.historyRows, months, now.toUTC(), now.zoneName ?? 'UTC');
    this.charges = chargesForPastPayments(this.historyRows, now);
    this.receiptStores = storesInCharges(this.charges);
    // the lines are built once per load, so an unreadable amount is reported once, not per redraw
    this.chargeLines = new Map(this.charges.map(row => [row, this.buildChargeLine(row)]));
  }

  /** Whether the coach has any recorded row at all; with none, one empty-history card shows. */
  get hasHistory(): boolean {
    return this.historyRows.length > 0;
  }

  /** The store plan's limit for the chart caption: the newest month that has one. */
  get chartCap(): number | undefined {
    return newestBarCap(this.clientBars);
  }

  /** The newest charges, the ones the folded Past Payments card shows. */
  get foldedCharges(): CoachHistoryRowView[] {
    return this.charges.slice(0, FOLDED_CHARGE_COUNT);
  }

  /** The charge's date, in the viewer's zone and language. */
  getChargeDate(row: CoachHistoryRowView): string {
    const at = DateTime.fromISO(row.at_utc, { zone: 'utc' }).toLocal();
    return at.isValid ? formatDateMed(at, BOOT_LOCALE) : '';
  }

  /** "€49.95 · 5 client seats", or "€29.99 · 5-client plan · App Store". */
  getChargeLine(row: CoachHistoryRowView): string {
    return this.chargeLines.get(row) ?? '';
  }

  /** Build one charge's amount line: the money, then what it paid for, then the store. */
  private buildChargeLine(row: CoachHistoryRowView): string {
    const copy = LL.coach.billing.recordedHistory;
    let amount: string;
    try {
      amount = formatMajorCurrencyAmount(row.amount ?? NaN, row.currency ?? '', BOOT_LOCALE);
    } catch (err) {
      // the server checked the currency is text, not that the browser knows it; show the stored
      // figures rather than nothing, and say so
      this.logging.trackSilentProblem('CoachChargeCurrencyUnreadable', { currency: row.currency, message: describeError(err) });
      amount = `${row.amount ?? ''} ${row.currency ?? ''}`.trim();
    }
    if (row.paymentRoute === CoachHistoryRoute.Paddle) {
      return row.paidSeats !== undefined ? copy.paddleLine({ amount, seats: row.paidSeats }) : copy.amountOnly({ amount });
    }
    const store = row.paymentRoute === CoachHistoryRoute.GooglePlay ? copy.googlePlay() : copy.appStore();
    return row.paidTierCap !== undefined
      ? copy.storeLine({ amount, cap: row.paidTierCap, store })
      : copy.storeLineNoPlan({ amount, store });
  }

  /** The line telling a store coach where that store keeps their receipts. */
  getReceiptsLine(store: CoachHistoryRoute.Apple | CoachHistoryRoute.GooglePlay): string {
    return store === CoachHistoryRoute.GooglePlay
      ? LL.coach.billing.recordedHistory.receiptsGoogle()
      : LL.coach.billing.recordedHistory.receiptsApple();
  }

  /**
   * The Invoice button of a Paddle charge. The link is fetched on every tap,
   * because it expires after an hour, and it is opened and dropped, never logged
   * or stored.
   *
   * On web, Safari blocks a window opened after an await, so an empty window is
   * opened at once, cut off from this page, and pointed at the link when it
   * arrives; it is closed again if the link does not come. On native the app
   * opens the link in the system browser once it has it.
   */
  async openInvoiceAsync(event: Event, row: CoachHistoryRowView) {
    // a tap inside a folded card would otherwise unfold it
    event.stopPropagation();
    const chargeId = row.chargeId;
    if (!chargeId || this.openingInvoiceFor) return;
    this.openingInvoiceFor = chargeId;
    // no 'noopener' here: with it the browser hands back no window to point at the link later
    const pendingWindow = this.isNative ? null : window.open('', '_blank');
    if (pendingWindow) {
      pendingWindow.opener = null;
    } else if (!this.isNative) {
      // the browser refused the tab; opening one after the fetch would be refused too, so say so
      this.openingInvoiceFor = undefined;
      this.logging.trackSilentProblem('CoachInvoiceWindowBlocked', { paymentRoute: row.paymentRoute });
      this.toastCtrl.showToast({
        message: LL.coach.billing.recordedHistory.invoiceBlocked(),
        type: ToastType.WarningLight,
        duration: 8000,
      });
      return;
    }
    try {
      const result = await this.coachBilling.getInvoiceUrlAsync(chargeId);
      if (!result.invoiceUrl) {
        throw new CoachInvoiceUnavailableError(result.error ?? 'no link');
      }
      if (pendingWindow) {
        pendingWindow.location.href = result.invoiceUrl;
      } else {
        await openExternalLink(result.invoiceUrl);
      }
      this.telemetry.trackEvent('coach_invoice_opened', { chargeId, paymentRoute: row.paymentRoute });
    } catch (err) {
      pendingWindow?.close();
      // the reason only, never the link
      this.logging.trackSilentProblem('CoachInvoiceOpenFailed', {
        reason: err instanceof CoachInvoiceUnavailableError ? err.message : describeError(err),
      });
      this.toastCtrl.showToast({
        message: LL.coach.billing.recordedHistory.invoiceUnavailable(),
        type: ToastType.WarningLight,
        duration: 6000,
      });
    } finally {
      this.openingInvoiceFor = undefined;
    }
  }

  // ── view-model accessors used by the template ──

  // ── fixed-tier (app-store) rail ──

  /** Current tier name, e.g. "5 clients". */
  getTierName(): string {
    if (!this.tierState) return '';
    return LL.coach.tier.tierName({ count: this.tierState.tierCap });
  }

  /** "Using X of Y client slots" line. */
  getTierUsageLine(): string {
    if (!this.tierState) return '';
    return LL.coach.tier.usageLine({ used: this.tierState.usedSlots, cap: this.tierState.tierCap });
  }

  /** Change Plan CTA only renders on store platforms (the cross-grade
   *  is an in-app store purchase). */
  get showChangePlanCta(): boolean {
    return this.isNative && this.tierState !== null;
  }

  // ── Paddle per-seat (web) rail ──

  /** Headline: "Coaching N clients" (N = active client seats). */
  getSeatHeading(): string {
    if (!this.seatState) return '';
    return LL.coach.billing.seat.heading({ count: this.seatState.clientSeats });
  }

  /**
   * "Next payment: $59.96 on 3 September" (#4455), or an empty string when we
   * cannot say both halves.
   *
   * The figures are a SNAPSHOT taken the last time the coach's seat count
   * changed, not a live quote — nothing refreshes them in between. That is what
   * this line is for: explaining the bill that follows a change the coach just
   * caused.
   *
   * **That snapshot is why the date must be checked against now, not merely
   * parsed.** Seat changes are irregular and renewals are monthly, so the
   * ordinary state of a stored date is that the charge it names has already
   * been taken. Drawing it anyway would tell a coach their next payment falls
   * on a day in the past, which is the common case rather than an edge one.
   *
   * Returns empty rather than a partial or stale line whenever any piece is
   * missing, unreadable or spent. The Paddle read that fills these can fail
   * too, and half a money sentence is worse than none: the template omits the
   * whole row instead.
   */
  getSeatNextPaymentLine(): string {
    const seat = this.seatState;
    if (!seat) return '';

    const amount = formatMinorCurrencyAmount(seat.nextBilledAmountMinor, seat.nextBilledCurrency, BOOT_LOCALE);
    if (!amount || !seat.nextBilledAt_utc) return '';

    // The stored date is UTC; a coach west of UTC would otherwise be told the
    // day after the one they are actually charged on.
    const billedAt = DateTime.fromISO(seat.nextBilledAt_utc, { zone: 'utc' }).toLocal();
    if (!billedAt.isValid) return '';
    // Already charged — see the note above. Compared against the START of
    // today rather than the exact moment, so the line does not vanish part-way
    // through the very day it names.
    if (billedAt < DateTime.local().startOf('day')) return '';

    return LL.coach.billing.seat.nextPayment({
      amount,
      date: formatDateMed(billedAt, BOOT_LOCALE),
    });
  }

  /**
   * Change Plan: load the tier catalog + live prices, present a picker of
   * the OTHER tiers, and run the in-app cross-grade. Reloads dashboard
   * state on success so the new tier/cap shows immediately.
   */
  async changePlanAsync() {
    // refuse to open the picker while a cross-grade is in flight — the
    // actual concurrency guard lives in applyTierChangeAsync, but blocking
    // re-open here keeps the UI honest
    if (this.isChangingPlan) return;

    let tiers: CoachTierOption[];
    try {
      tiers = await this.coachTier.loadTiersAsync();
    } catch (err) {
      this.logging.trackAbnormalException(err);
      return;
    }
    if (this.viewDestroyed) return;

    // offer every buyable tier except the one the coach is already on
    const currentTierId = this.tierState?.tierId;
    const choices = tiers.filter(t => t.product !== null && t.entry.tierId !== currentTierId);
    if (choices.length === 0) {
      await this.alertCtrl.showAlertAsync({
        title: LL.coach.tier.changePlan(),
        message: LL.coach.tier.noTiersAvailable(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
      });
      return;
    }

    const opts: PopupMenuOptions = { buttons: [] };
    for (const tier of choices) {
      opts.buttons.push({
        text: LL.coach.tier.tierOption({ count: tier.entry.cap, price: tier.product!.priceText }),
        icon: 'people',
        handler: () => {
          void this.applyTierChangeAsync(tier);
        },
      });
    }
    void this.popupCtrl.createMenu(opts);
  }

  /**
   * Warn the coach, and let them back out, when the plan they picked holds
   * fewer clients than they currently have (#4454).
   *
   * Three possible answers, and the caller must tell them apart:
   *
   * - `{ count, names }` — the coach was warned and chose to go ahead. The
   *   caller reports those clients again in the success message.
   * - `undefined` — go ahead with no mention. Either nobody would be paused, or
   *   the client list could not be read.
   * - `null` — the coach cancelled. The caller must do nothing at all.
   *
   * A failure to read the client list does NOT block the plan change. The
   * change is what the coach asked for, and refusing it because a local read
   * failed would be the worse outcome — so that case answers `undefined`, the
   * same as "no warning needed", and the failure is reported rather than
   * swallowed.
   *
   * Which clients would be paused is decided by the shared `computeClientsToPause`
   * so this warning and the queued-downgrade notice cannot disagree.
   */
  private async confirmPauseWarningAsync(
    tier: CoachTierOption,
  ): Promise<{ count: number; names: string } | undefined | null> {
    let slots: CoachClientSlot[];
    try {
      // A one-off read, so no live watcher: this list builds one alert and is
      // then thrown away, and nothing on this page redraws when it changes.
      slots = await this.userDb.database.withDocWatcherAsync(
        watcher => this.userDb.database.getByIdHeadAsync('coach-client/', watcher)) as CoachClientSlot[];
    } catch (err) {
      this.logging.trackSilentProblem('coach-tier-change-warning-slot-read-failed', {
        message: describeError(err),
      });
      return undefined;
    }

    const pause = computeClientsToPause(slots ?? [], tier.entry.cap);
    if (!pause) return undefined;

    const confirmed = await this.alertCtrl.showAlertAsync({
      title: LL.coach.tier.pauseWarning.title(),
      message: LL.coach.tier.pauseWarning.message({
        count: pause.count,
        names: pause.names,
        cap: tier.entry.cap,
      }),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.coach.tier.pauseWarning.confirm(), role: AlertButtonRole.Primary, result: true },
      ],
    });
    if (!confirmed) return null;
    return { count: pause.count, names: pause.names };
  }

  private async applyTierChangeAsync(tier: CoachTierOption) {
    // Ask BEFORE the re-entrancy flag is set, so the Change Plan button is not
    // left disabled behind a dialog the coach is still reading.
    // Guard the CONFIRMATION window too, not only the purchase (#4454). The
    // dialog below is open for as long as the coach reads it, and without this
    // a second tap would raise a second dialog whose own confirmation could
    // start a second store purchase after the first one finished.
    if (this.isConfirmingPlanChange || this.isChangingPlan) return;
    this.isConfirmingPlanChange = true;
    let willPause: { count: number; names: string } | undefined | null;
    try {
      willPause = await this.confirmPauseWarningAsync(tier);
    } finally {
      this.isConfirmingPlanChange = false;
    }
    // null means the coach cancelled. undefined means go ahead silently.
    if (willPause === null) return;

    // re-entrancy guard: a second picker tap (or a stale picker) must not
    // kick off a concurrent cross-grade. The flag also disables the
    // Change Plan button for the duration.
    if (this.isChangingPlan) return;
    this.isChangingPlan = true;
    this.cd.markForCheck();
    try {
      const result = await this.coachTier.changeTierAsync(tier);
      switch (changePlanOutcomeMessage(result)) {
        case CoachTierOutcomeMessage.None:
          return;
        case CoachTierOutcomeMessage.AlreadyLinked:
          // the store subscription belongs to a different app account — surface
          // which one
          await showCoachAlreadyLinkedAlertAsync(this.alertCtrl, result.linkedEmail);
          return;
        case CoachTierOutcomeMessage.OtherAccount:
          // the store says a different one of our accounts started this purchase,
          // and no account we can name holds it (#5384)
          await showOtherAccountAlertAsync(this.alertCtrl);
          return;
        case CoachTierOutcomeMessage.StoreRefused:
          // the store refuses this token every time, so "try again" would be
          // advice that cannot work (#6243)
          this.logging.trackSilentProblem('CoachTierChangeStoreRefused', { error: result.error });
          await showStoreRefusedAlertAsync(this.alertCtrl);
          return;
        case CoachTierOutcomeMessage.ConfirmationPending:
          // the store made the change and our confirmation never got an answer, so
          // we do not know that it failed and a charged coach must not be told it
          // did. Like the coach-tier purchase message, this one says since #5053
          // that the change MAY finish applying at the next app open, because the
          // server parks a claim and re-asks the store at the next sign-in. The
          // same reason for hedging applies — see
          // CoachTierActionResult.confirmationFailedAfterPurchase. See
          // design-docs/iap-subscriptions.md, "The same rule, everywhere else".
          this.logging.trackSilentProblem('CoachTierChangeNotConfirmed', { error: result.error });
          await this.alertCtrl.showAlertAsync({
            title: LL.coach.tier.changePlan(),
            message: LL.coach.tier.changeNotYetConfirmed(),
            buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
          });
          return;
        // changePlanOutcomeMessage never returns the first two; they are listed so
        // the switch names every case
        case CoachTierOutcomeMessage.AlreadyOwned:
        case CoachTierOutcomeMessage.CrossProviderConflict:
        case CoachTierOutcomeMessage.Failed:
          this.logging.trackAbnormalError(result.error ?? 'unknown', LL.coach.tier.changeFailed());
          return;
        case CoachTierOutcomeMessage.Succeeded:
          break;
      }
      // A queued (end-of-cycle) Apple downgrade vs an immediate change.
      //
      // On the IMMEDIATE path the clients have already been paused by the time
      // this alert appears, so say so and say where to undo it (#4454). On the
      // scheduled path nothing has happened yet — the coach's client list
      // carries a standing reminder for the whole waiting period instead.
      const outcome = result.scheduled ? LL.coach.tier.changeScheduled() : LL.coach.tier.changeSuccess();
      const pausedSuffix = !result.scheduled && willPause
        ? LL.coach.tier.pausedNowSuffix({ count: willPause.count, names: willPause.names })
        : '';
      await this.alertCtrl.showAlertAsync({
        title: LL.coach.tier.changePlan(),
        // Both halves are translated; the wrapper only re-brands the join, and
        // the space between them is added HERE rather than being carried as a
        // leading space inside the translated suffix — a leading space is
        // exactly the kind of thing a later translation pass drops silently.
        message: untranslatedString(pausedSuffix ? `${outcome} ${pausedSuffix}` : outcome),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
      });
      // refresh so the dashboard reflects the new tier/cap (immediate case)
      await this.loadStateAsync();
    } finally {
      this.isChangingPlan = false;
      this.cd.markForCheck();
    }
  }

  /** Pick one of three balance phrases driven by R12.2. */
  getBalancePhrase(): string {
    if (!this.state) return '';
    const balance = this.state.balanceClientMonths;
    const formatted = this.formatClientMonths(Math.abs(balance));
    if (balance > 0) {
      return LL.coach.billing.balance.remaining({ amount: formatted });
    }
    if (this.state.hasPaymentMethodOnFile) {
      return LL.coach.billing.balance.dueWithBilling({ amount: formatted });
    }
    return LL.coach.billing.balance.locked();
  }

  /** Secondary line under the balance phrase: active-slot count +
   *  projected end-of-month consumption. */
  getActiveSlotsLine(): string {
    if (!this.state) return '';
    return LL.coach.billing.activeSlotsLine({ count: this.state.activeSlotCount });
  }

  /** When the coach has a positive balance AND no payment method,
   *  surface the runout-date projection per R12.2. */
  getRunoutLine(): string {
    if (!this.state?.estimatedRunoutDate_utc) return '';
    return LL.coach.billing.runoutLine({ date: this.formatDate(this.state.estimatedRunoutDate_utc) });
  }

  /** History row's settle-outcome subtext: "Settled" if the coach
   *  had a payment method when the cron ran, "Unsettled" otherwise. */
  getHistoryRowSubtext(row: CoachBillingMonthlyStatementSummary): string {
    return row.hasPaymentMethodOnFile
      ? LL.coach.billing.history.settled()
      : LL.coach.billing.history.unsettled();
  }

  /** Friendly "March 2026" label for a `YYYY-MM` period string. */
  getPeriodLabel(period: string): string {
    const dt = parseMonthKey(period);
    if (!dt.isValid) return period;
    return formatMonthYearLong(dt, BOOT_LOCALE);
  }

  /** Human-readable consumed-this-month line for a history row. */
  getHistoryConsumedLine(row: CoachBillingMonthlyStatementSummary): string {
    return LL.coach.billing.history.consumed({ amount: this.formatClientMonths(row.consumedClientMonths) });
  }

  private formatClientMonths(value: number): string {
    // one decimal place; the dashboard is informational and does not
    // need to expose fractional cents
    return value.toFixed(1);
  }

  private formatDate(isoString: string): string {
    const dt = DateTime.fromISO(isoString, { zone: 'utc' });
    if (!dt.isValid) return isoString;
    return formatDateMed(dt, BOOT_LOCALE);
  }
}
